import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Code, Globe, CheckCircle, ArrowRight, ExternalLink } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const appTypes = [
  { value: 'web_app', label: 'Web Application' },
  { value: 'mobile_app', label: 'Mobile Application (iOS / Android)' },
  { value: 'desktop_app', label: 'Desktop Application' },
  { value: 'ecommerce', label: 'E-Commerce Platform' },
  { value: 'erp_crm', label: 'ERP / CRM System' },
  { value: 'api_integration', label: 'API / Integration' },
  { value: 'other', label: 'Other' },
];

const budgetRanges = [
  { value: 'under_5k', label: '< $5,000' },
  { value: '5k_15k', label: '$5,000 - $15,000' },
  { value: '15k_50k', label: '$15,000 - $50,000' },
  { value: '50k_plus', label: '$50,000+' },
  { value: 'discuss', label: 'To be discussed' },
];

export default function AppsHostingPage() {
  const { t } = useLanguage();
  const [selectedService, setSelectedService] = useState<'apps' | 'hosting' | null>(null);
  const [form, setForm] = useState({
    company_name: '', contact_name: '', contact_email: '', contact_phone: '',
    app_type: '', project_name: '', description: '', features: '',
    timeline: '', budget: '', design_reference: '', technical_requirements: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/api/quotes`, {
        product_category: 'hosting',
        product_name: `Apps Dev: ${form.app_type} - ${form.project_name}`,
        quantity: 1,
        details: `Project: ${form.project_name}\nType: ${form.app_type}\nDescription: ${form.description}\nFeatures: ${form.features}\nTimeline: ${form.timeline}\nBudget: ${form.budget}\nDesign Ref: ${form.design_reference}\nTech Requirements: ${form.technical_requirements}`,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone,
        company_name: form.company_name,
      });
      setSubmitted(true);
    } catch {} finally { setLoading(false); }
  };

  const inputStyle: React.CSSProperties = { width: '100%', padding: '12px 14px', border: '1px solid var(--border)', fontSize: 14, outline: 'none', boxSizing: 'border-box' as const, background: 'white', fontFamily: 'var(--font-body)' };
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' };

  if (submitted) {
    return (
      <><Header />
        <main style={{ paddingTop: 72, minHeight: '100vh', background: 'var(--bg-section)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div data-testid="apps-success" style={{ background: 'white', border: '1px solid var(--border)', padding: '64px 48px', textAlign: 'center', maxWidth: 500 }}>
            <CheckCircle size={56} style={{ color: 'var(--primary)', marginBottom: 20 }} />
            <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 24, fontWeight: 700, marginBottom: 12 }}>Project Request Submitted</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 15, marginBottom: 32 }}>Our development team will review your project and contact you within 48 hours with a detailed proposal.</p>
            <button onClick={() => { setSubmitted(false); setSelectedService(null); }}
              style={{ padding: '14px 32px', background: 'var(--primary)', color: 'white', border: 'none', fontWeight: 600, cursor: 'pointer', textTransform: 'uppercase' }}>
              Back to Services
            </button>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main style={{ paddingTop: 72, minHeight: '100vh', background: 'var(--bg-section)' }}>
        {/* Hero */}
        <div style={{ background: 'linear-gradient(135deg, #0A1128 0%, #1A237E 100%)', padding: '64px 24px', textAlign: 'center' }}>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 16 }}>
            <Code size={36} style={{ color: 'rgba(255,255,255,0.8)' }} strokeWidth={1.5} />
            <Globe size={36} style={{ color: 'rgba(255,255,255,0.8)' }} strokeWidth={1.5} />
          </div>
          <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(28px,4vw,44px)', fontWeight: 700, color: 'white', marginBottom: 12 }}>
            Apps Development & Web Hosting
          </h1>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.75)', maxWidth: 550, margin: '0 auto' }}>
            From custom application development to reliable web hosting solutions.
          </p>
        </div>

        {!selectedService ? (
          /* Service Selection */
          <div style={{ maxWidth: 900, margin: '0 auto', padding: '64px 24px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }} className="service-choice-grid">
              {/* Apps Development */}
              <button data-testid="select-apps-dev" onClick={() => setSelectedService('apps')}
                style={{ background: 'white', border: '2px solid var(--border)', padding: '48px 32px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.08)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}>
                <Code size={48} style={{ color: 'var(--primary)', marginBottom: 20 }} strokeWidth={1.5} />
                <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 24, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12 }}>Apps Development</h2>
                <p style={{ color: 'var(--text-secondary)', fontSize: 15, lineHeight: 1.6, marginBottom: 20 }}>
                  Custom web, mobile, and desktop applications built to your specifications.
                </p>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--primary)', fontWeight: 600, fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Get Started <ArrowRight size={16} />
                </span>
              </button>

              {/* Web Hosting */}
              <a href="https://www.maluzhosting.org" target="_blank" rel="noopener noreferrer" data-testid="select-hosting"
                style={{ background: 'white', border: '2px solid var(--border)', padding: '48px 32px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s', display: 'block', textDecoration: 'none' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--secondary)'; e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.08)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}>
                <Globe size={48} style={{ color: 'var(--secondary)', marginBottom: 20 }} strokeWidth={1.5} />
                <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 24, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12 }}>Web Hosting</h2>
                <p style={{ color: 'var(--text-secondary)', fontSize: 15, lineHeight: 1.6, marginBottom: 20 }}>
                  Professional hosting services at maluzhosting.org — reliable, fast and secure.
                </p>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--secondary)', fontWeight: 600, fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Visit maluzhosting.org <ExternalLink size={16} />
                </span>
              </a>
            </div>
          </div>
        ) : (
          /* Apps Development Form */
          <div style={{ maxWidth: 780, margin: '0 auto', padding: '48px 24px' }}>
            <button onClick={() => setSelectedService(null)} style={{ fontSize: 14, color: 'var(--primary)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 6 }}>
              &larr; Back to selection
            </button>

            <div style={{ background: 'white', border: '1px solid var(--border)', padding: '40px' }}>
              <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Apps Development Quote Request</h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 32 }}>Tell us about your project and we'll provide a detailed proposal.</p>

              <form onSubmit={handleSubmit} data-testid="apps-dev-form">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  {/* Contact */}
                  <div style={{ padding: '20px', background: 'var(--bg-section)', border: '1px solid var(--border)' }}>
                    <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>Contact Information</p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }} className="form-2col">
                      <div><label style={labelStyle}>Company *</label><input data-testid="apps-company" value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} required style={inputStyle} /></div>
                      <div><label style={labelStyle}>Contact Person *</label><input data-testid="apps-contact" value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})} required style={inputStyle} /></div>
                      <div><label style={labelStyle}>Email *</label><input data-testid="apps-email" type="email" value={form.contact_email} onChange={e => setForm({...form, contact_email: e.target.value})} required style={inputStyle} /></div>
                      <div><label style={labelStyle}>Phone</label><input data-testid="apps-phone" value={form.contact_phone} onChange={e => setForm({...form, contact_phone: e.target.value})} style={inputStyle} /></div>
                    </div>
                  </div>

                  {/* Project */}
                  <div style={{ padding: '20px', background: 'var(--bg-section)', border: '1px solid var(--border)' }}>
                    <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>Project Details</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }} className="form-2col">
                        <div><label style={labelStyle}>Project Name *</label><input data-testid="apps-project" value={form.project_name} onChange={e => setForm({...form, project_name: e.target.value})} required style={inputStyle} /></div>
                        <div><label style={labelStyle}>Application Type *</label>
                          <select data-testid="apps-type" value={form.app_type} onChange={e => setForm({...form, app_type: e.target.value})} required style={{...inputStyle, cursor: 'pointer'}}>
                            <option value="">Select type...</option>
                            {appTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                        </div>
                      </div>
                      <div><label style={labelStyle}>Project Description *</label><textarea data-testid="apps-desc" value={form.description} onChange={e => setForm({...form, description: e.target.value})} required rows={4} placeholder="Describe your project goals and target audience..." style={{...inputStyle, resize: 'vertical' as const}} /></div>
                      <div><label style={labelStyle}>Key Features</label><textarea value={form.features} onChange={e => setForm({...form, features: e.target.value})} rows={3} placeholder="List the main features you need..." style={{...inputStyle, resize: 'vertical' as const}} /></div>
                      <div><label style={labelStyle}>Technical Requirements</label><textarea value={form.technical_requirements} onChange={e => setForm({...form, technical_requirements: e.target.value})} rows={2} placeholder="Specific technologies, integrations, APIs..." style={{...inputStyle, resize: 'vertical' as const}} /></div>
                    </div>
                  </div>

                  {/* Budget & Timeline */}
                  <div style={{ padding: '20px', background: 'var(--bg-section)', border: '1px solid var(--border)' }}>
                    <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>Budget & Timeline</p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }} className="form-2col">
                      <div><label style={labelStyle}>Budget Range</label>
                        <select value={form.budget} onChange={e => setForm({...form, budget: e.target.value})} style={{...inputStyle, cursor: 'pointer'}}>
                          <option value="">Select range...</option>
                          {budgetRanges.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                        </select>
                      </div>
                      <div><label style={labelStyle}>Expected Timeline</label><input value={form.timeline} onChange={e => setForm({...form, timeline: e.target.value})} placeholder="e.g., 3 months" style={inputStyle} /></div>
                    </div>
                    <div style={{ marginTop: 16 }}>
                      <label style={labelStyle}>Design Reference / Inspiration</label>
                      <input value={form.design_reference} onChange={e => setForm({...form, design_reference: e.target.value})} placeholder="Links to websites or apps you like..." style={inputStyle} />
                    </div>
                  </div>

                  <button type="submit" data-testid="apps-submit-btn" disabled={loading}
                    style={{ padding: '16px', background: 'var(--primary)', color: 'white', fontWeight: 700, fontSize: 15, border: 'none', textTransform: 'uppercase', letterSpacing: '0.08em', cursor: 'pointer', opacity: loading ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <Code size={18} /> Submit Project Request
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <style>{`
          @media (max-width: 600px) {
            .service-choice-grid { grid-template-columns: 1fr !important; }
            .form-2col { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </main>
      <Footer />
    </>
  );
}
